import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Box, Grid } from "@mui/system";
import { IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import AnomaliesMissingData from "./AnomaliesTabs/AnomaliesMissingData/AnomaliesMissingData";
import AnomaliesFtd from "./AnomaliesTabs/AnomaliesFtd/AnomaliesFtd";
import AnomaliesBrands from "./AnomaliesTabs/AnomaliesBrands/AnomaliesBrands";
import AnomaliesIncome from "./AnomaliesTabs/AnomaliesIncome/AnomaliesIncome";
import AnomaliesEarnings from "./AnomaliesTabs/AnomaliesEarnings/AnomaliesEarnings";
import { useBizDevelopmentDashboard } from "../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";

const ANOMALIES_TAB_KEYS = [
  "missing_data",
  "ftd",
  "brands",
  "income",
  "earnings",
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
// Mapping of tab index to refresh data tracker
const REFRESH_DATA_TRACKER: any = {
  0: "871",
  1: "875",
  2: "880",
  3: "884",
  4: "889",
};

const AnomaliesTabs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const anomaliesTabFromUrl = searchParams.get("anomaliesTab");
  // Read the initial tab from the URL if present, otherwise default to the
  // first tab (Missing Data). We deliberately do NOT write the param here, so
  // the default tab is never reflected in the URL on initial load.
  const initialTab = anomaliesTabFromUrl
    ? Math.max(ANOMALIES_TAB_KEYS.indexOf(anomaliesTabFromUrl), 0)
    : 0;
  const [value, setValue] = React.useState(initialTab);

  const {
    fetchMissingData,
    fetchFtdData,
    fetchAnomaliesBrandData,
    fetchAnomaliesIncomeData,
    fetchAnomaliesEarningsData,
  } = useBizDevelopmentDashboard();

  //check if Missing data is already fetched
  const isFetchedForMissingData = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.isFetchedForMissingData,
  );

  //check if FTD data is already fetched
  const isFetchedForFtdData = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.isFetchedForFtdData,
  );

  //check if Brand data is already fetched
  const isFetchedForBrandData = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.isFetchedForBrandData,
  );

  //check if Income data is already fetched
  const isFetchedForIncomeData = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.isFetchedForIncomeData,
  );

  //check if Earning data is already fetched
  const isFetchedForEarningData = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.isFetchedForEarningData,
  );

  //check if the data is loading
  const isLoading = useSelector(
    (state: any) => state.bizDevelopmentDashboard.isLoading,
  );

  // Refresh the active tab by re-calling its fetcher.
  const handleRefresh = () => {
    if (value === 0) fetchMissingData();
    if (value === 1) fetchFtdData();
    if (value === 2) fetchAnomaliesBrandData();
    if (value === 3) fetchAnomaliesIncomeData();
    if (value === 4) fetchAnomaliesEarningsData();
  };

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);

    if (newValue === 0 && !isFetchedForMissingData) {
      fetchMissingData();
    }
    if (newValue === 1 && !isFetchedForFtdData) {
      fetchFtdData();
    }
    if (newValue === 2 && !isFetchedForBrandData) {
      fetchAnomaliesBrandData();
    }

    if (newValue === 3 && !isFetchedForIncomeData) {
      fetchAnomaliesIncomeData();
    }

    if (newValue === 4 && !isFetchedForEarningData) {
      fetchAnomaliesEarningsData();
    }
    // The param is only added once the user actively switches tabs.
    setSearchParams((prev) => {
      prev.set("anomaliesTab", ANOMALIES_TAB_KEYS[newValue]);
      return prev;
    });
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Grid container spacing={2} alignItems="center" p={2}>
          <Grid size={6}>
            <Box display={"flex"} alignItems={"center"}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
                sx={{
                  background: "#F5F5F5",
                  borderRadius: "8px",
                  padding: "4px",
                  minHeight: "30px",
                  "& .MuiTab-root": {
                    color: "#00000061",
                    padding: "5px 16px",
                    minHeight: "30px",
                    "&.Mui-selected": {
                      color: "#000000DE",
                      background: "#ffffff",
                      borderRadius: "4px",
                    },
                  },

                  /* Active line color */
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#9C27B0",
                    height: "0px", // optional
                    borderRadius: "10px", // optional
                  },
                }}
              >
                <Tab
                  label="MISSING DATA"
                  data-tracker="869"
                  {...a11yProps(0)}
                />
                <Tab label="FTD" data-tracker="873" {...a11yProps(1)} />
                <Tab label="BRANDS" data-tracker="878" {...a11yProps(2)} />
                <Tab label="INCOME" data-tracker="882" {...a11yProps(3)} />
                <Tab label="Earnings" data-tracker="887" {...a11yProps(4)} />
              </Tabs>
            </Box>
          </Grid>
          <Grid size={6}>
            <Box display={"flex"} justifyContent={"flex-end"}>
              <IconButton
                data-tracker={REFRESH_DATA_TRACKER[value]}
                aria-label="refresh"
                color="secondary"
                onClick={handleRefresh}
                disabled={isLoading}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <CustomTabPanel value={value} index={0}>
          <AnomaliesMissingData />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <AnomaliesFtd />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <AnomaliesBrands />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={3}>
          <AnomaliesIncome />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={4}>
          <AnomaliesEarnings />
        </CustomTabPanel>
      </Box>
    </>
  );
};

export default AnomaliesTabs;
